import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { useI18n } from '../i18n'
import DashboardLayout from '../components/DashboardLayout'
import RequestModal from '../components/RequestModal'
import { readableText } from '../lib/color'
import { GiftIcon as GiftGlyph, suggestIcon } from '../lib/giftIcons'
import { findOpenRequest } from '../lib/chat'
import { nickSuffix } from '../lib/nick'

// ─── Badge ──────────────────────────────────────────────────────

const Badge = ({ children }) => (
  <span style={{
    fontFamily: "'DM Mono', monospace", fontSize: 13,
    padding: '2px 8px', borderRadius: 6,
    background: 'var(--surface-2)', color: 'var(--text-muted)',
    border: '1px solid var(--border)', whiteSpace: 'nowrap',
  }}>
    {children}
  </span>
)

// ─── Icons (inline SVG) ─────────────────────────────────────────

const FilterIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
)

const Chevron = ({ open }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease' }}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
)

const GiftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 12 20 22 4 22 4 12" />
    <rect x="2" y="7" width="20" height="5" />
    <line x1="12" y1="22" x2="12" y2="7" />
    <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
    <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
  </svg>
)

// ─── Scope segmented control (grouped pill) ─────────────────────

const ScopeSegment = ({ value, onChange, options }) => (
  <div style={{
    display: 'inline-flex', gap: 3, padding: 3,
    background: 'var(--surface-2)', borderRadius: 10,
  }}>
    {options.map(o => {
      const active = value === o.value
      return (
        <button key={o.value} onClick={() => onChange(o.value)} style={{
          fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 500,
          padding: '7px 14px', borderRadius: 7, border: 'none', cursor: 'pointer',
          background: active ? 'var(--surface)' : 'transparent',
          color: active ? 'var(--text-primary)' : 'var(--text-muted)',
          boxShadow: active ? '0 1px 2px rgba(0,0,0,0.18)' : 'none',
          transition: 'all 150ms ease', whiteSpace: 'nowrap',
        }}>
          {o.label}
        </button>
      )
    })}
  </div>
)

// ─── Type dropdown ──────────────────────────────────────────────

const TypeDropdown = ({ value, options, onChange, label }) => {
  const [open, setOpen] = useState(false)
  const current = options.find(o => o.value === value)
  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        fontFamily: "'DM Sans', sans-serif", fontSize: 15,
        color: 'var(--text-primary)',
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 8, padding: '8px 12px', cursor: 'pointer',
        minHeight: 40, whiteSpace: 'nowrap',
      }}>
        <FilterIcon />
        <span>{label}: {current?.label}</span>
        <Chevron open={open} />
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 90 }} />
          <div style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 100,
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 10, padding: 4, minWidth: 190,
            boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
          }}>
            {options.map(o => {
              const active = value === o.value
              return (
                <button key={o.value} onClick={() => { onChange(o.value); setOpen(false) }} style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  fontFamily: "'DM Sans', sans-serif", fontSize: 16,
                  color: active ? 'var(--text-primary)' : 'var(--text-muted)',
                  fontWeight: active ? 500 : 400,
                  background: active ? 'var(--surface-2)' : 'transparent',
                  border: 'none', borderRadius: 7, padding: '11px 12px',
                  cursor: 'pointer', minHeight: 44,
                }}>
                  {o.label}
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

// ─── Empty-state card ───────────────────────────────────────────

const EmptyState = ({ filter, t, onOffer }) => {
  const isCircle = filter === 'trust_circle'
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
      <div style={{
        maxWidth: 420, width: '100%', textAlign: 'center',
        border: '1px dashed var(--border)', borderRadius: 20, padding: 32,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: '50%',
          background: 'var(--surface-2)', color: 'var(--text-muted)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16,
        }}>
          <GiftIcon />
        </div>
        <h3 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 17, fontWeight: 500, marginBottom: 8 }}>
          {filter === 'disaster' ? t.disaster.empty : isCircle ? t.explore.emptyCircleTitle : t.explore.emptyOtherTitle}
        </h3>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: 'var(--text-muted)', lineHeight: 1.6, maxWidth: 320, marginBottom: 20 }}>
          {filter === 'disaster' ? '' : isCircle ? t.explore.emptyCircleText : t.explore.emptyOtherText}
        </p>
        <button className="btn-primary" onClick={onOffer} style={{ borderRadius: 8 }}>
          + {t.explore.offerGift}
        </button>
      </div>
    </div>
  )
}

// ─── Gift card ───────────────────────────────────────────────────

const GiftCard = ({ gift, onRequest, onOpen, onProfile, onReport, reported, communities = [], t }) => {
  const hex = gift.creator?.hex_code || gift.creator_hex || '#888888'
  const r = parseInt(hex.slice(1,3),16)
  const g = parseInt(hex.slice(3,5),16)
  const b = parseInt(hex.slice(5,7),16)

  const typeBadge = () => {
    if (gift.gift_type === 'once')  return <Badge>{t.give.badgeOnce}</Badge>
    if (gift.gift_type === 'quota') return <Badge>{t.give.badgeQuota(gift.quota_remaining ?? gift.quota ?? 0)}</Badge>
    return null // unlimited — no badge needed
  }

  return (
    <div className="armagan-card" style={{ background: `rgba(${r},${g},${b},0.10)`, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
      <button
        onClick={() => onProfile(gift.creator_id)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 0, minWidth: 0 }}
      >
        <div style={{ width: 24, height: 24, borderRadius: '50%', background: hex, flexShrink: 0 }} />
        <span className="mono" style={{ textDecoration: 'underline', textDecorationColor: 'var(--border)' }}>{hex}{nickSuffix(gift.creator?.nickname)}</span>
      </button>
      {communities.length > 0 && (
        <span title={communities.join(' · ')} style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 1, minWidth: 0 }}>
          {communities.join(' · ')}
        </span>
      )}
      </div>
      <div onClick={() => onOpen(gift.id)} style={{ cursor: 'pointer', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: hex, color: readableText(hex), display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '2px 0 14px' }}>
          <GiftGlyph id={gift.icon || suggestIcon(`${gift.title || ''} ${gift.description || ''}`)} size={34} />
        </div>
        <h3 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 20, fontWeight: 500, marginBottom: 8, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{gift.title}</h3>
      </div>

      {/* Badges row */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
        {gift.is_disaster && (
          <Badge>{`${t.disaster.badge}${t.disaster.categories[gift.disaster_category] ? ': ' + t.disaster.categories[gift.disaster_category] : ''}`}</Badge>
        )}
        {gift.is_disaster && gift.expires_at && (
          <Badge>{t.disaster.daysLeft(Math.ceil((new Date(gift.expires_at) - Date.now()) / 86400000))}</Badge>
        )}
        {gift.created_at && Date.now() - new Date(gift.created_at).getTime() < 7 * 86400000 && <Badge>{t.explore.newBadge}</Badge>}
        {typeBadge()}
        {gift.is_physical && <Badge>{t.give.badgePhysical}</Badge>}
      </div>

      <button
        className="btn-primary"
        onClick={() => onRequest(gift)}
        style={{ width: '100%', background: hex, color: readableText(hex), borderRadius: 8, marginTop: 'auto' }}
      >
        {t.explore.requestSupport}
      </button>
      {gift.is_disaster && (
        <div style={{ textAlign: 'right', marginTop: 10 }}>
          <button
            onClick={() => !reported && onReport(gift.id)}
            disabled={reported}
            style={{ background: 'none', border: 'none', cursor: reported ? 'default' : 'pointer', padding: 0, fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: 'var(--text-muted)', textDecoration: reported ? 'none' : 'underline' }}
          >
            {reported ? t.disaster.reported : t.disaster.report}
          </button>
        </div>
      )}
    </div>
  )
}

// ─── Add-to-circle modal ────────────────────────────────────────

const AddToCircleModal = ({ data, onAdd, onSkip }) => {
  const { t } = useI18n()
  const [note, setNote] = useState('')
  const hex = data.providerHex || '#888888'
  const r = parseInt(hex.slice(1,3),16)
  const g = parseInt(hex.slice(3,5),16)
  const b = parseInt(hex.slice(5,7),16)
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 20, padding: 32, maxWidth: 380, width: '100%', border: '1px solid var(--border)', textAlign: 'center' }}>
        <div style={{ width: 52, height: 52, borderRadius: '50%', background: `rgba(${r},${g},${b},0.15)`, border: `2px solid rgba(${r},${g},${b},0.4)`, margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 28, height: 28, borderRadius: '50%', background: hex }} />
        </div>
        <h2 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 20, fontWeight: 500, marginBottom: 8 }}>{t.explore.addToCircle}</h2>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: 'var(--text-muted)', marginBottom: 8 }}>{t.explore.addToCircleHint}</p>
        {data.giftTitle && (
          <p style={{ fontFamily: "'DM Mono', monospace", fontSize: 14, color: 'var(--text-muted)', marginBottom: 28 }}>"{data.giftTitle}"</p>
        )}
        <div style={{ textAlign: 'left', margin: '0 0 24px' }}>
          <label style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>{t.explore.thanksLabel}</label>
          <textarea rows={3} maxLength={280} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t.explore.thanksPlaceholder} style={{ width: '100%', boxSizing: 'border-box' }} />
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: 'var(--text-muted)', margin: '6px 0 0' }}>{t.explore.thanksHint}</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn-secondary" onClick={() => onSkip(note)} style={{ flex: 1 }}>{t.explore.addToCircleSkip}</button>
          <button className="btn-primary"   onClick={() => onAdd(note)}  style={{ flex: 1 }}>{t.explore.addToCircleYes}</button>
        </div>
      </div>
    </div>
  )
}

// ─── Main page ─────────────────────────────────────────────────

const Al = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { t } = useI18n()
  const [activeTab, setActiveTab]       = useState('al')
  const [filter, setFilter]             = useState('community')
  const [typeFilter, setTypeFilter]     = useState('all')
  const [availableGifts, setAvailableGifts] = useState([])
  const [receivedSupport, setReceivedSupport] = useState([])
  const [circleIds, setCircleIds]       = useState([])
  const [communityIds, setCommunityIds] = useState([])
  const [commList, setCommList]       = useState([])   // my communities: { id, name }
  const [commMembers, setCommMembers]   = useState({})   // community id -> member ids
  const [commFilter, setCommFilter]     = useState('all')
  const [loading, setLoading]           = useState(true)
  const [requestGift, setRequestGift]   = useState(null)
  const [confirmModal, setConfirmModal] = useState(null)
  const [reportedIds, setReportedIds] = useState([])

  const fetchCircleIds = useCallback(async () => {
    if (!user) return
    const { data } = await supabase.from('trust_circle').select('member_id').eq('owner_id', user.id)
    setCircleIds(data?.map(r => r.member_id) || [])
  }, [user])

  const fetchCommunityIds = useCallback(async () => {
    if (!user) return
    const { data: myComms } = await supabase
      .from('sub_community_members')
      .select('sub_community_id, sub_communities(id, name, owner_id, members_aware)')
      .eq('user_id', user.id)
    const mine = (myComms || []).filter((r) => r.sub_communities)
    if (mine.length === 0) { setCommunityIds([]); setCommList([]); setCommMembers({}); return }
    const commIds = mine.map(r => r.sub_community_id)
    const { data: members } = await supabase.from('sub_community_members').select('sub_community_id, user_id').in('sub_community_id', commIds)
    const byComm = {}
    for (const m of members || []) (byComm[m.sub_community_id] ||= []).push(m.user_id)
    setCommList(mine.map((r) => {
      const c = r.sub_communities
      return { id: c.id, name: c.owner_id === user.id || c.members_aware ? c.name : t.community.unnamed }
    }))
    setCommMembers(byComm)
    setCommunityIds([...new Set((members || []).map(r => r.user_id).filter(id => id !== user.id))])
  }, [user, t])

  const fetchData = useCallback(async () => {
    if (!user) return
    setLoading(true)
    try {
      if (activeTab === 'al') {
        let query = supabase
          .from('gifts')
          .select('*, creator:creator_id(*)')
          .eq('is_active', true)
          .neq('creator_id', user.id)

        if (filter === 'disaster') {
          query = query.eq('is_disaster', true).eq('hidden', false).gt('expires_at', new Date().toISOString())
        } else {
          query = query.eq('is_disaster', false)
          if (filter === 'trust_circle') {
            if (circleIds.length === 0) { setAvailableGifts([]); setLoading(false); return }
            query = query.in('creator_id', circleIds)
          } else {
            // Community is the only other scope — global is disabled for pilot.
            const ids = commFilter !== 'all' && commMembers[commFilter]
              ? commMembers[commFilter].filter((m) => m !== user.id)
              : communityIds
            if (ids.length === 0) { setAvailableGifts([]); setLoading(false); return }
            query = query.in('creator_id', ids)
          }
        }

        // Type filter
        if (typeFilter === 'unlimited') query = query.eq('gift_type', 'unlimited')
        else if (typeFilter === 'quota') query = query.eq('gift_type', 'quota')
        else if (typeFilter === 'once')  query = query.eq('gift_type', 'once')
        else if (typeFilter === 'physical') query = query.eq('is_physical', true)

        const { data, error } = await query.order('created_at', { ascending: false })
        if (error) throw error
        setAvailableGifts(data || [])
      } else {
        const { data, error } = await supabase
          .from('support_transactions')
          .select('*, provider:provider_id(*), gift:gift_id(title), receiver_confirmed, confirmed_at')
          .eq('receiver_id', user.id)
          .order('created_at', { ascending: false })
        if (error) throw error
        setReceivedSupport(data || [])
      }
    } catch (err) { console.error('fetchData error:', err) }
    finally { setLoading(false) }
  }, [user, activeTab, filter, typeFilter, circleIds, communityIds, commFilter, commMembers])

  useEffect(() => { if (user) { fetchCircleIds(); fetchCommunityIds() } }, [user, fetchCircleIds, fetchCommunityIds])
  useEffect(() => { if (user) fetchData() }, [user, fetchData])

  const handleRequest = async (gift) => {
    const existing = await findOpenRequest(gift.id, user.id)
    if (existing) { navigate(`/chat/${existing}`); return }
    setRequestGift(gift)
  }

  const handleReport = async (giftId) => {
    setReportedIds((ids) => [...ids, giftId])
    try {
      const { error } = await supabase.from('gift_reports').insert({ gift_id: giftId, reporter_id: user.id })
      if (error && error.code !== '23505') throw error
    } catch (err) { console.error('handleReport error:', err) }
  }

  const handleMarkReceived = async (tx) => {
    try {
      await supabase.from('support_transactions').update({ receiver_confirmed: true, confirmed_at: new Date().toISOString() }).eq('id', tx.id)
      setReceivedSupport(prev => prev.map(s => s.id === tx.id ? { ...s, receiver_confirmed: true } : s))
      setConfirmModal({ transactionId: tx.id, providerId: tx.provider_id, providerHex: tx.provider?.hex_code, giftTitle: tx.gift?.title })
    } catch (err) { console.error('handleMarkReceived error:', err) }
  }

  const saveThanks = async (note) => {
    const body = (note || '').trim()
    if (!body || !confirmModal) return
    const { error } = await supabase.rpc('add_thanks', { p_transaction: confirmModal.transactionId, p_body: body })
    if (error) console.error('add_thanks error:', error)
  }

  const handleSkipCircle = async (note) => {
    await saveThanks(note)
    setConfirmModal(null)
  }

  const handleAddToCircle = async (note) => {
    if (!confirmModal) return
    await saveThanks(note)
    try {
      await supabase.from('trust_circle').upsert(
        { owner_id: user.id, member_id: confirmModal.providerId, gift_title: confirmModal.giftTitle || null },
        { onConflict: 'owner_id,member_id' }
      )
      fetchCircleIds()
    } catch (err) { console.error('handleAddToCircle error:', err) }
    finally { setConfirmModal(null) }
  }

  const tabStyle = (active) => ({
    fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 500,
    color: active ? 'var(--text-primary)' : 'var(--text-muted)',
    background: 'none', border: 'none',
    borderBottom: active ? '2px solid var(--user-color)' : '2px solid transparent',
    padding: '12px 0', cursor: 'pointer', marginRight: 24, transition: 'color 150ms ease',
  })

  const typeFilters = [
    { value: 'all',      label: t.explore.filterAll },
    { value: 'unlimited',label: t.explore.filterUnlimited },
    { value: 'quota',    label: t.explore.filterQuota },
    { value: 'once',     label: t.explore.filterOnce },
    { value: 'physical', label: t.explore.filterPhysical },
  ]

  return (
    <DashboardLayout>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 32, fontWeight: 500, marginBottom: 6 }}>{t.explore.title}</h1>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: 'var(--text-muted)', margin: 0 }}>{commList.length > 0 ? commList.map((c) => c.name).join(' · ') : t.explore.subtitle}</p>
      </div>

      <div style={{ borderBottom: '1px solid var(--border)', marginBottom: 16 }}>
        <button style={tabStyle(activeTab === 'al')} onClick={() => setActiveTab('al')}>{t.explore.tabFeed}</button>
        <button style={tabStyle(activeTab === 'aldiklarim')} onClick={() => setActiveTab('aldiklarim')}>{t.explore.tabHistory}</button>
      </div>

      {/* Single-row filter: scope segment (left) + type dropdown (right) */}
      {activeTab === 'al' && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: 12, flexWrap: 'wrap', marginBottom: 28,
        }}>
          <ScopeSegment
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'trust_circle', label: t.explore.filterTrustCircle },
              { value: 'community',    label: t.explore.filterCommunity },
              { value: 'disaster',     label: t.disaster.tab },
            ]}
          />
          <TypeDropdown
            value={typeFilter}
            onChange={setTypeFilter}
            label={t.explore.typeLabel}
            options={typeFilters}
          />
        </div>
      )}

      {activeTab === 'al' && filter === 'community' && commList.length > 1 && (
        <div style={{ marginTop: -12, marginBottom: 24 }}>
          <ScopeSegment
            value={commFilter}
            onChange={setCommFilter}
            options={[{ value: 'all', label: t.explore.filterAll }, ...commList.map((c) => ({ value: c.id, label: c.name }))]}
          />
        </div>
      )}

      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontFamily: "'DM Sans', sans-serif", fontSize: 16 }}>—</p>
      ) : activeTab === 'al' ? (
        availableGifts.length === 0 ? (
          <EmptyState filter={filter} t={t} onOffer={() => navigate('/give')} />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gridAutoRows: 310, gap: 16 }}>
            {availableGifts.map(gift => (
              <GiftCard key={gift.id} gift={gift} onRequest={handleRequest} onOpen={(gid) => navigate(`/gift/${gid}`)} onProfile={(uid) => navigate(`/member/${uid}`)} onReport={handleReport} reported={reportedIds.includes(gift.id)} communities={commList.filter((c) => (commMembers[c.id] || []).includes(gift.creator_id)).map((c) => c.name)} t={t} />
            ))}
          </div>
        )
      ) : (
        receivedSupport.length === 0 ? (
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: 'var(--text-muted)', padding: '64px 0' }}>{t.explore.historyEmpty}</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {receivedSupport.map(s => (
              <div key={s.id} style={{ background: 'var(--surface)', borderRadius: 16, padding: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: s.provider?.hex_code || 'var(--surface-2)', flexShrink: 0 }} />
                  <div>
                    <p style={{ fontFamily: "'DM Mono', monospace", fontSize: 15, margin: 0 }}>{s.provider?.hex_code || '—'}{nickSuffix(s.provider?.nickname)}</p>
                    <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: 'var(--text-muted)', margin: 0 }}>{s.gift?.title}</p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 14, color: 'var(--text-muted)' }}>
                    {new Date(s.created_at).toLocaleDateString()}
                  </span>
                  <button className="btn-ghost" onClick={() => navigate(`/chat/${s.id}`)} style={{ fontSize: 14 }}>{t.chat.open}</button>
                  {!s.receiver_confirmed ? (
                    <button className="btn-ghost" onClick={() => handleMarkReceived(s)} style={{ fontSize: 14 }}>
                      {t.explore.markReceived}
                    </button>
                  ) : (
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: 'rgba(80,200,120,0.8)' }}>✓</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {requestGift && (
        <RequestModal
          gift={requestGift}
          t={t}
          onClose={() => setRequestGift(null)}
          onDone={(txId) => { setRequestGift(null); navigate(`/chat/${txId}`) }}
        />
      )}

      {confirmModal && (
        <AddToCircleModal
          data={confirmModal}
          onAdd={handleAddToCircle}
          onSkip={handleSkipCircle}
        />
      )}
    </DashboardLayout>
  )
}

export default Al
